import React, { useState } from "react";
import { validateLogin } from "../../helpers/Validator";
import { IMAGES } from "../../assets/Images/Image";
import "./Login.css";
import { useTranslation } from "react-i18next";
import { CacheManager } from "../../services/local/CacheManager";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/slices/AuthSlice";
import { FiMail, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Dialog from "../components/Dialog";
import { CONSTANTS } from "../../utils/Constants";
import { WSAuthentication } from "../../services/remote/authentication/WSAuthentication";
import { Link } from "react-router-dom";

const Login: React.FC = () => {
  const cache = CacheManager.getInstance();
  const authInstance = WSAuthentication.getInstance();

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<any>({});
  const [serverError, setServerError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [open, setOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError("");
    setErrors({});

    const validationErrors = validateLogin(email, password);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsLoading(true);
      const user = await authInstance.login({ email, password });
      if (user?.token) {
        dispatch(loginSuccess({ token: user.token, user }));
        window.location.reload();
      }
    } catch (error: any) {
      switch (error.message) {
        case CONSTANTS.STRINGS.INVALID_CREDENTIALS: {
          setServerError(t("login.invalid_credentials"));
          setOpen(true);
          break;
        }
        default: {
          setServerError(t("login.unexpected_error"));
          setOpen(true);
          break;
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="container">
      <div className="header">
        <LanguageSwitcher />
      </div>

      <div className="formContainer">
        <form className="form" onSubmit={handleSubmit} noValidate>
          <h1 className="mc-title">{t("login.title")}</h1>
          <p className="text">{t("login.sign_in")}</p>

          {serverError && <div className="error-server">{serverError}</div>}

          <div className="formGroup">
            <label htmlFor="email" className="label">
              {t("login.email")}
            </label>
            <div className="input-container">
              <FiMail className="input-icon" />
              <input
                id="email"
                type="email"
                placeholder={t("login.email_placeholder")}
                value={email}
                className={`input ${errors.email ? "input-error" : ""}`}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
              />
            </div>
            {errors.email && <span className="error">{errors.email}</span>}
          </div>

          <div className="formGroup">
            <div className="label-container">
              <label htmlFor="password" className="label">
                {t("login.password")}
              </label>
            </div>
            <div className="input-container">
              <FiLock className="input-icon" />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder={t("login.password_placeholder")}
                value={password}
                className={`input ${errors.password ? "input-error" : ""}`}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="password-toggle"
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {errors.password && (
              <span className="error">{errors.password}</span>
            )}
          </div>

          <button type="submit" className="mc-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="loading"></span>
                {t("login.logging_in")}
              </>
            ) : (
              t("login.login_button")
            )}
          </button>

          {/* Register Button */}

          <div className="register-container">
            <p
              className="register-link"
              onClick={() =>
                (window.location.href = "https://vendora-shop.online/register")
              }
            >
              {t("login.dont_have_account")}
            </p>
          </div>

          <Link to="/forgot-password" className="forgotPassword">
            {t("login.forgot_password")}
          </Link>
        </form>
      </div>

      <div className="infoContainer">
        <img
          src={IMAGES.AUTH_BACKGROUND || "/default-auth-bg.jpg"}
          className="image"
          alt="Authentication Background"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = "/default-auth-bg.jpg";
          }}
        />
        <div className="infoTextContainer">
          <h2 className="infoTitle">{t("login.welcome_back")}</h2>
          <p className="infoDescription">{t("login.welcome_message")}</p>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t("login.dialog_title")}
        description={serverError || t("login.dialog_message")}
        showYesButton={false}
      />
    </div>
  );
};

export default Login;
